import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Mono,
  RadioGroup,
  RadioGroupItem,
  Text,
} from "@lagoon/ui";

import { api, ApiError } from "../api.js";
import { LoadingPanel, Notice } from "./feedback.js";
import { Link } from "../router.js";

/**
 * Where deploy outcomes go.
 *
 * Before this existed a deploy reported its fate only to whoever was watching
 * the page. Email is off by default because a product that mails everybody
 * about everything teaches them to filter it.
 *
 * The webhook secret is shown once, at creation, and there is no way to read
 * it back: a receiver that loses it sets a new URL and gets a new secret,
 * which is a rotation rather than a recovery.
 */

/**
 * The three-way choice, as a value the radio group can carry.
 *
 * `null` is a real option here, not the absence of one: "follow my account" is
 * what makes the account switch reach sites nobody has opened. A radio group's
 * value is a string, so the three states are named rather than stringified.
 */
const CHOICES = [
  {
    key: "follow",
    value: null,
    label: "Follow my account setting",
    // Filled in from the account's own setting, which is the only hint here
    // that depends on data rather than on the option.
    hint: "",
  },
  {
    key: "always",
    value: true,
    label: "Always email me about this site",
    hint: "Including when a deploy fails, with the reason.",
  },
  {
    key: "never",
    value: false,
    label: "Never email me about this site",
    hint: "Even if your account setting is on.",
  },
] as const;

type ChoiceKey = (typeof CHOICES)[number]["key"];

function keyFor(value: boolean | null): ChoiceKey {
  return value === null ? "follow" : value ? "always" : "never";
}

/** The sentence voice for a label beside a control, not the field-label voice. */
const OPTION_LABEL =
  "lg:font-sans lg:text-body-sm lg:normal-case lg:tracking-normal lg:text-foreground";

export function Notifications(props: {
  siteId: string;
  navigate: (to: string) => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [webhook, setWebhook] = useState<string | null>(null);
  /**
   * The choice the user just made, shown before the server confirms it.
   *
   * Without this the radio stays on the old option until the mutation and its
   * refetch complete, so clicking one appears to do nothing for a moment.
   * Wrapped in an object rather than held bare because `null` is a real value
   * here (follow the account) and could not otherwise be told from "nothing
   * pending".
   */
  const [pending, setPending] = useState<{ value: boolean | null } | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  /**
   * Whether the secret has been copied (LGN-128).
   *
   * The button gave no acknowledgement at all, for a value shown exactly once:
   * somebody who was not sure whether the click landed had no way to check
   * except to press it again and hope. `passes.tsx` already does this.
   */
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const settings = useQuery({
    queryKey: ["notifications", props.siteId],
    queryFn: () => api.getNotifications(props.siteId),
  });

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ["notifications", props.siteId] });

  const save = useMutation({
    mutationFn: (input: { notifyOnDeploy?: boolean | null; webhookUrl?: string | null }) =>
      api.updateNotifications(props.siteId, input),
    onSuccess: async (result) => {
      setError(null);
      setPending(null);
      // Only present when a new URL was set, and only this once.
      if (result.webhookSecret !== null) {
        setSecret(result.webhookSecret);
        setCopied(false);
      }
      setWebhook(null);
      await invalidate();
    },
    onError: (caught) => {
      // Snap back to what the server still believes, rather than leaving the
      // radio showing a choice that did not take.
      setPending(null);
      setError(caught instanceof ApiError ? caught.message : "Could not save that.");
    },
  });

  if (settings.isPending) {
    return <LoadingPanel label="Loading notification settings" />;
  }
  if (settings.isError) {
    return <Notice tone="danger">Could not load notification settings.</Notice>;
  }

  const current = settings.data;
  const choice = pending === null ? current.notifyOnDeploy : pending.value;

  function choose(value: boolean | null): void {
    setPending({ value });
    save.mutate({ notifyOnDeploy: value });
  }

  function submitWebhook(event: FormEvent): void {
    event.preventDefault();
    save.mutate({ webhookUrl: (webhook ?? "").trim() || null });
  }

  return (
    <div className="lg:flex lg:flex-col lg:gap-5">
      <Card interactive={false}>
        <div className="lg:flex lg:flex-col lg:gap-5">
          <CardHeader>
            <CardTitle as="h2">Notifications</CardTitle>
            <CardDescription>
              What happens when a deploy finishes, or the live release changes. Deploy emails are
              off until you ask for them, here or for your whole account in{" "}
              <Link to="/settings" navigate={props.navigate}>
                account settings
              </Link>
              .
            </CardDescription>
          </CardHeader>

          {error === null ? null : <Notice tone="danger">{error}</Notice>}

          <RadioGroup
            aria-label="Deploy emails for this site"
            value={keyFor(choice)}
            onValueChange={(next) => {
              const option = CHOICES.find((entry) => entry.key === next);
              if (option !== undefined) choose(option.value);
            }}
          >
            {CHOICES.map((option) => (
              <div key={option.key} className="lg:flex lg:items-start lg:gap-3">
                <RadioGroupItem
                  value={option.key}
                  id={`notify-${option.key}`}
                  className="lg:mt-0.5"
                />
                <div className="lg:flex lg:flex-col lg:gap-1">
                  <Label htmlFor={`notify-${option.key}`} className={OPTION_LABEL}>
                    {option.label}
                  </Label>
                  <Text as="span" size="caption" tone="subtle">
                    {option.key === "follow"
                      ? current.accountNotifyOnDeploy
                        ? "Your account is set to email you. This site will."
                        : "Your account is set to no emails. This site will not."
                      : option.hint}
                  </Text>
                </div>
              </div>
            ))}
          </RadioGroup>
        </div>
      </Card>

      <Card interactive={false}>
        <div className="lg:flex lg:flex-col lg:gap-5">
          <CardHeader>
            <CardTitle>Webhook</CardTitle>
            <CardDescription>
              We POST a signed JSON body to your endpoint on every deploy event. Optional.
            </CardDescription>
          </CardHeader>

          {secret === null ? null : (
            <Alert tone="success">
              <AlertTitle>Copy this signing secret now</AlertTitle>
              <AlertDescription className="lg:flex lg:flex-col lg:gap-3">
                <span>
                  Verify it against the <Mono>X-Lagoon-Signature</Mono> header. It cannot be shown
                  again; setting a new URL issues a new secret.
                </span>
                <span className="lg:flex lg:flex-wrap lg:gap-3">
                  <Input
                    className="lg:min-w-56 lg:flex-1 lg:font-mono lg:text-caption"
                    readOnly
                    value={secret}
                    aria-label="Webhook signing secret"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void navigator.clipboard.writeText(secret).then(() => setCopied(true));
                    }}
                  >
                    {copied ? "Copied" : "Copy"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSecret(null);
                    }}
                  >
                    Done
                  </Button>
                </span>
              </AlertDescription>
            </Alert>
          )}

          <form className="lg:flex lg:flex-wrap lg:gap-3" onSubmit={submitWebhook}>
            <Input
              className="lg:min-w-56 lg:flex-1"
              type="url"
              value={webhook ?? current.webhookUrl ?? ""}
              onChange={(event) => {
                setWebhook(event.target.value);
              }}
              placeholder="https://hooks.example.com/site-events"
              aria-label="Webhook URL"
            />
            <Button variant="outline" type="submit" disabled={save.isPending}>
              Save
            </Button>
            {current.webhookUrl === null ? null : (
              <Button
                type="button"
                variant="ghost"
                disabled={save.isPending}
                onClick={() => {
                  save.mutate({ webhookUrl: null });
                }}
              >
                Remove
              </Button>
            )}
          </form>
          <Text size="caption" tone="subtle">
            Must be https and publicly reachable. Private and internal addresses are refused.
          </Text>
        </div>
      </Card>

      <Card interactive={false}>
        <div className="lg:flex lg:flex-col lg:gap-4">
          <CardTitle>Recent deliveries</CardTitle>
          {current.deliveries.length === 0 ? (
            <Text size="sm" tone="muted">
              Nothing sent yet.
            </Text>
          ) : (
            <ul className="lg:flex lg:flex-col lg:gap-px lg:overflow-hidden lg:rounded-sm lg:border lg:border-hairline lg:bg-hairline">
              {current.deliveries.map((delivery) => (
                <li
                  key={delivery.id}
                  className="lg:flex lg:flex-wrap lg:items-start lg:gap-4 lg:bg-card lg:px-4 lg:py-3"
                >
                  <div className="lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-1">
                    <span className="lg:flex lg:flex-wrap lg:items-center lg:gap-2">
                      <Mono className="lg:text-caption lg:text-strong">{delivery.event}</Mono>
                      <Badge tone={delivery.status === "sent" ? "success" : "danger"}>
                        {delivery.status}
                        {delivery.statusCode === null ? "" : ` ${delivery.statusCode}`}
                      </Badge>
                    </span>
                    <Text as="span" size="caption" tone="subtle">
                      {delivery.channel} → {delivery.target}
                    </Text>
                    {delivery.error === null ? null : (
                      <Text as="span" size="caption" tone="subtle">
                        {delivery.error}
                      </Text>
                    )}
                  </div>
                  <Mono className="lg:text-micro lg:text-subtle">
                    {new Date(delivery.createdAt).toLocaleString()}
                  </Mono>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </div>
  );
}
